import { Button } from '@aether/ui/Button';
import { Icon } from '@aether/ui/Icon';
import { getAppOrigin } from '@aether/utils/services/linking-service';
import { Link, useRouteLoaderData } from '@remix-run/react';

export const handle = {
  newLayout: true,
};

export default function New() {
  const data = useRouteLoaderData<{ origin: string }>('root');

  return (
    <>
      <section className="pb-20 pt-32">
        <div className="container mx-auto px-6 text-center">
          <h1 className="mb-8 text-6xl font-bold md:text-7xl">
            Self-learning <em className="not-italic text-gray-500 dark:text-gray-400">Portfolios</em>.
          </h1>
          <p className="mb-12 text-xl text-gray-800 dark:text-gray-400">
            Full suite of portfolio management tools, driven by AI.
          </p>
          <div className="mt-12 flex items-center justify-center gap-8">
            <img src="/images/home/EBI_c.webp" alt="EBI logo" className="block h-12 w-auto grayscale dark:invert" />
            <img src="/images/home/HSBC_c.webp" alt="HSBC logo" className="block h-12 w-auto grayscale dark:invert" />
            <img src="/images/home/HPW_c.webp" alt="HPW logo" className="block h-12 w-auto grayscale dark:invert" />
            <img src="/images/home/MB_c.webp" alt="MB logo" className="block h-12 w-auto grayscale dark:invert" />
            <img src="/images/home/DWS_c.webp" alt="DWS logo" className="block h-12 w-auto grayscale dark:invert" />
            <img src="/images/home/E_c.webp" alt="DWS logo" className="block h-12 w-auto grayscale dark:invert" />
          </div>
          <div className="mt-12 flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            AI Portfolios at the <em className="not-italic text-gray-500 dark:text-gray-400">click of a button</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">Access a library of 50k funds.</p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm8frdq2o03hwz40iwqtmec31"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 dark:bg-black">
        <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
          Performance and <em className="not-italic text-gray-500 dark:text-gray-400"> live track record</em>.
        </h2>
        <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
          Funds managed by the platform have been top-rated by independent third parties.
        </p>
        <div className="container mx-auto max-w-4xl px-6">
          <div className="mb-12">
            <h3 className="mb-4 text-xl font-medium">
              Morningstar rating
              <div className="text-base text-gray-400">As of February 2025</div>
            </h3>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-[2fr_1fr_1fr] gap-8">
                <div className="text-base text-gray-600 dark:text-gray-400">Fund Name</div>
                <div className="text-base text-gray-600 dark:text-gray-400">Overall Rating</div>
                <div className="text-base text-gray-600 dark:text-gray-400">Category</div>
              </div>

              <div className="grid grid-cols-[2fr_1fr_1fr] gap-8">
                <div className="text-base">
                  <a href="/fund-documentation?fund=ge-flex-usd" className="hover:underline">
                    Arabesque SICAV - Global ESG Momentum Flexible Allocation - (USD)
                  </a>
                </div>
                <div className="flex">
                  <span className="text-yellow-500">★★★★★</span>
                </div>
                <div className="text-base">USD Flexible Allocation</div>
              </div>

              <div className="grid grid-cols-[2fr_1fr_1fr] gap-8">
                <div className="text-base">
                  <a href="/fund-documentation?fund=ge-eq-gbp-plus" className="hover:underline">
                    Arabesque SICAV - Global ESG Equity GBP PLUS
                  </a>
                </div>
                <div className="flex">
                  <span className="text-yellow-500">★★★★★</span>
                </div>
                <div className="text-base">Global Large-Cap Blend Equity</div>
              </div>

              <div className="grid grid-cols-[2fr_1fr_1fr] gap-8">
                <div className="text-base">
                  <a href="/fund-documentation?fund=ge-flex-eur" className="hover:underline">
                    Arabesque SICAV - Global ESG Momentum Flexible Allocation - (EUR)
                  </a>
                </div>
                <div className="flex">
                  <span className="text-yellow-500">★★★★</span>
                </div>
                <div className="text-base">USD Flexible Allocation</div>
              </div>

              <div className="grid grid-cols-[2fr_1fr_1fr] gap-8">
                <div className="text-base">
                  <a href="/fund-documentation?fund=ge-eq-gbp" className="hover:underline">
                    Arabesque SICAV - Global ESG Equity GBP
                  </a>
                </div>
                <div className="flex">
                  <span className="text-yellow-500">★★★★</span>
                </div>
                <div className="text-base">Global Large-Cap Blend Equity</div>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            Build portfolios at the <em className="not-italic text-gray-500 dark:text-gray-400">speed of thought</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
            Create and analyze portfolios in seconds using our intuitive interface.
          </p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm8sntwzd0dnxy6twge0xlxxk"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 dark:bg-black">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            Upload and <em className="not-italic text-gray-500 dark:text-gray-400">rebalance portfolios</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
            Import your portfolio and optimize it with our powerful rebalancing engine.
          </p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm8snqhvz0dmny6twf5kx7hwa"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            Analyze the <em className="not-italic text-gray-500 dark:text-gray-400">source of performance</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
            Use our attribution engine to pinpoint the source of performance using proprietary risk factor models.
          </p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm8ft4cjf00cz080incb528pk"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 dark:bg-black">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            Interact using <em className="not-italic text-gray-500 dark:text-gray-400">Dragon Assistant</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
            Take your portfolio to the next level using GenAI. Receive recommendations and rebalancing insights.
          </p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm8ft4g6p03vuz40i8hkzah3m"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      <section className="bg-gray-50 py-20 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="mb-12 text-center text-5xl font-bold md:text-6xl">
            Comprehensive <em className="not-italic text-gray-500 dark:text-gray-400">Portfolio Optimiser</em>.
          </h2>
          <p className="mb-12 text-center text-xl text-gray-600 dark:text-gray-400">
            A comprehensive portfolio optimisation engine for your advanced rebalancing needs.
          </p>
          <div className="mb-12 flex justify-center">
            <div className="h-[475px] w-[1040px]">
              <iframe
                src="https://app.supademo.com/embed/cm934y6em35ugeyp02rfbpg12"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Button className="rounded-full bg-gray-950 px-8 py-3 font-normal text-white transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-300 dark:hover:text-gray-950">
              <Link to={`${getAppOrigin(data?.origin)}/auth/register`}>Try for free</Link>
            </Button>
            <Button className="rounded-full border border-gray-200 bg-gray-100 px-8 py-3 font-normal text-gray-500 transition hover:bg-gray-200 hover:text-gray-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-400">
              <Link to="/docs/about-us/contact-us" target="_blank" rel="noopener noreferrer">
                Contact us <Icon icon="ic:outline-open-in-new" className="ml-1 inline-block" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
